import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, CircularProgress, Alert,
  MenuItem, TextField, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import ModuleHeader from './components/ModuleHeader';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_URL } from './config';

interface Ciclo { id: number; nome: string; ativo: boolean; }
interface Area  { id: number; nome: string; zona: string; }
interface Agente { id: number; nome: string; matricula: string; }

interface LinhaGeral {
  se: number; data_inicio: string; data_fim: string;
  imoveis_informados: number; quarteirao_concluido: number;
  hdt: number; media_imoveis_agente: number; total_registros: number;
  imoveis_residencia: number; imoveis_comercio: number; imoveis_terreno_baldio: number;
  imoveis_ponto_estrategico: number; imoveis_outros: number; total_imoveis: number;
  trat_focal: number; trat_perifocal: number; inspecionados: number;
  recuperados: number; recusados: number; fechados: number;
  amostras_coletadas: number;
  dep_a1: number; dep_a2: number; dep_b: number; dep_c: number;
  dep_d1: number; dep_d2: number; dep_e: number; dep_total: number;
  dep_eliminados: number; dep_tratados: number;
  larvicida_total_gramas: number;
  imoveis_positivos: number; dep_positivos: number;
}

interface Totais {
  total_registros: number; hdt: number; media_imoveis_agente: number;
  imoveis_informados: number; quarteirao_concluido: number;
  imoveis_residencia: number; imoveis_comercio: number; imoveis_terreno_baldio: number;
  imoveis_ponto_estrategico: number; imoveis_outros: number; total_imoveis: number;
  trat_focal: number; trat_perifocal: number; inspecionados: number;
  recuperados: number; recusados: number; fechados: number;
  amostras_coletadas: number;
  dep_a1: number; dep_a2: number; dep_b: number; dep_c: number;
  dep_d1: number; dep_d2: number; dep_e: number; dep_total: number;
  dep_eliminados: number; dep_tratados: number;
  larvicida_total_gramas: number;
  imoveis_positivos: number; dep_positivos: number;
}

interface ApiData { linhas: LinhaGeral[]; totais: Totais; ses_disponiveis: number[]; }

const fmtData = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

// ──── Paleta Designer (tema escuro) ────
const C = {
  bg:        '#030b16',
  bgPanel:   '#071323',
  bgRow:     '#0a1727',
  bgRowAlt:  '#0c1c30',
  navy:      '#0d1c35',
  cyan:      '#00b8cc',
  cyanDim:   'rgba(0,184,204,0.12)',
  cyanBorder:'rgba(0,184,204,0.22)',
  orange:    '#e85d26',
  white:     '#f0f4f8',
  gray:      '#7a8799',
  grayLt:    '#b0bcc8',
  border:    'rgba(255,255,255,0.07)',
  borderMid: 'rgba(255,255,255,0.18)',
  green:     '#22c55e',
  purple:    '#8b5cf6',
  amber:     '#f59e0b',
};

const FIELD_SX = {
  minWidth: 180,
  '& .MuiInputBase-root': {
    backgroundColor: '#0a1830',
    color: C.white,
    fontFamily: "'Inter', sans-serif",
  },
  '& .MuiInputBase-input': { color: C.white },
  '& .MuiInputLabel-root': {
    color: C.grayLt,
    fontFamily: "'Inter', sans-serif",
  },
  '& .MuiInputLabel-root.Mui-focused': { color: C.cyan },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.cyanBorder },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.cyan },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: C.cyan,
    borderWidth: '1px',
  },
  '& .MuiSelect-icon': { color: C.grayLt },
};

const PERIODO_HEADER_SX = {
  bgcolor: '#0a1830',
  color: C.cyan,
  fontWeight: 700,
  fontSize: 10.5,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  fontFamily: "'Inter', sans-serif",
  borderBottom: `1px solid ${C.cyanBorder}`,
  borderRight: `2px solid ${C.cyanBorder}`,
};
const PERIODO_CELL_SX = {
  bgcolor: '#0a1727',
  color: '#f59e0b',
  fontWeight: 700,
  fontSize: 14,
  fontFamily: "'Inter', sans-serif",
  borderRight: `2px solid ${C.cyanBorder}`,
};

const COL_GROUPS = [
  { label: 'SEMANA EPIDEMIOLÓGICA', color: C.grayLt, bg: '#DBEAFE', bodyColor: 'transparent', cols: [
      { key: 'se',                   label: 'SE',      fmt: (v: any, _r: LinhaGeral) => 'SE ' + v },
      { key: '_periodo',             label: 'Periodo', fmt: (_v: any, r: LinhaGeral) => fmtData(r.data_inicio) + ' - ' + fmtData(r.data_fim), headerSx: PERIODO_HEADER_SX, cellSx: PERIODO_CELL_SX },
      { key: 'imoveis_informados',   label: 'Im.Inf.', fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'quarteirao_concluido', label: 'Qtd.C',   fmt: (v: any, _r: LinhaGeral) => v > 0 ? v : '—' },
      { key: 'total_registros',      label: 'FAD-01s', fmt: (v: any, _r: LinhaGeral) => v },
  ]},
  { label: 'IMOVEIS TRABALHADOS', color: C.cyan, bg: '#D1FAE5', bodyColor: 'transparent', cols: [
      { key: 'imoveis_residencia',        label: 'Resid.',    fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'imoveis_comercio',          label: 'Com.',      fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'imoveis_terreno_baldio',    label: 'T.Baldio',  fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'imoveis_ponto_estrategico', label: 'P.Estrat.', fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'imoveis_outros',            label: 'Outro',     fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'total_imoveis',             label: 'TOTAL',     fmt: (v: any, _r: LinhaGeral) => v },
  ]},
  { label: 'SITUAÇÃO DOS IMÓVEIS', color: C.amber, bg: '#FEF3C7', bodyColor: 'transparent', cols: [
      { key: 'trat_focal',     label: 'Tr.Focal',  fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'trat_perifocal', label: 'Tr.Perif.', fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'inspecionados',      label: 'Inspec.',   fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'amostras_coletadas', label: 'Amostras',  fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'recusados',      label: 'Recusa',    fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'fechados',       label: 'Fechados',  fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'recuperados',        label: 'Recuper.',  fmt: (v: any, _r: LinhaGeral) => v },
  ]},
  { label: 'DEPOSITOS INSPECIONADOS', color: C.green, bg: '#D1FAE5', bodyColor: 'transparent', cols: [
      { key: 'dep_a1', label: 'A1', fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'dep_a2', label: 'A2', fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'dep_b',  label: 'B',  fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'dep_c',  label: 'C',  fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'dep_d1', label: 'D1', fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'dep_d2', label: 'D2', fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'dep_e',  label: 'E',  fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'dep_total',      label: 'Total',   fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'dep_eliminados', label: 'Elimin.', fmt: (v: any, _r: LinhaGeral) => v },
  ]},
  { label: 'LARVICIDA L1+L2', color: C.orange, bg: '#DBEAFE', bodyColor: 'transparent', cols: [
      { key: 'larvicida_total_gramas', label: 'Gramas', fmt: (v: any, _r: LinhaGeral) => v > 0 ? parseFloat(v).toFixed(1).replace('.', ',') + ' g' : '—' },
  ]},
  { label: '', color: '#f97316', bg: 'transparent', bodyColor: 'transparent', cols: [
      { key: 'dep_tratados', label: 'Trat.', fmt: (v: any, _r: LinhaGeral) => v },
  ]},
  { label: 'POSITIVOS', color: C.orange, bg: '#FEE2E2', bodyColor: 'transparent', cols: [
      { key: 'imoveis_positivos', label: 'Imov.+', fmt: (v: any, _r: LinhaGeral) => v },
      { key: 'dep_positivos',     label: 'Dep.+',  fmt: (v: any, _r: LinhaGeral) => v },
  ]},
];

const ALL_COLS = COL_GROUPS.flatMap((g: any) => g.cols);

const TC = ({ children, align = 'center', sx = {}, ...rest }: any) => (
  <TableCell align={align} {...rest} sx={{
    px: 0.8,
    py: 0.9,
    fontSize: '0.82rem',
    whiteSpace: 'nowrap',
    fontFamily: "'Inter', sans-serif",
    fontVariantNumeric: 'tabular-nums',
    color: C.white,
    borderBottom: `2px solid ${C.borderMid}`,
    borderRight: `2px solid ${C.borderMid}`,
    ...sx
  }}>
    {children}
  </TableCell>
);

export default function ResumoGeral({ onVoltar }: { onVoltar: () => void }) {
  const { token } = useAuth();
  const headers = { Authorization: 'Bearer ' + token };
  const [ciclos, setCiclos]     = useState<Ciclo[]>([]);
  const [areas, setAreas]       = useState<Area[]>([]);
  const [agentes, setAgentes]   = useState<Agente[]>([]);
  const [cicloId, setCicloId]   = useState('');
  const [areaId, setAreaId]     = useState('');
  const [agenteId, setAgenteId] = useState('');
  const [seFiltro, setSeFiltro] = useState<string>('');
  const [dados, setDados]       = useState<ApiData | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro]         = useState('');
  const [celulaAtiva, setCelulaAtiva] = useState<{linha: number, coluna: number} | null>(null);

  useEffect(() => {
    axios.get(API_URL + '/api/ciclos/', { headers }).then(r => {
      setCiclos(r.data);
      const ativo = r.data.find((c: Ciclo) => c.ativo);
      if (ativo) setCicloId(String(ativo.id));
    });
    axios.get(API_URL + '/api/areas/', { headers }).then(r => setAreas(r.data));
    axios.get(API_URL + '/api/agentes/', { headers }).then(r => setAgentes(r.data));
  }, []);

  const buscar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const params: any = {};
      if (cicloId)  params.ciclo_id  = cicloId;
      if (areaId)   params.area_id   = areaId;
      if (agenteId) params.agente_id = agenteId;
      if (seFiltro) params.se        = seFiltro;
      const r = await axios.get(API_URL + '/api/resumos/geral', { headers, params });
      setDados(r.data);
    } catch { setErro('Erro ao carregar dados.'); }
    finally { setCarregando(false); }
  }, [cicloId, areaId, agenteId, seFiltro]);

  useEffect(() => { buscar(); }, [buscar]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!celulaAtiva) return;
      const totalLinhas = dados?.linhas?.length ?? 0;
      const totalColunas = ALL_COLS.length;
      let { linha, coluna } = celulaAtiva;
      if (e.key === 'ArrowUp')         { linha = Math.max(0, linha - 1); e.preventDefault(); }
      else if (e.key === 'ArrowDown')  { linha = Math.min(totalLinhas - 1, linha + 1); e.preventDefault(); }
      else if (e.key === 'ArrowLeft')  { coluna = Math.max(0, coluna - 1); e.preventDefault(); }
      else if (e.key === 'ArrowRight') { coluna = Math.min(totalColunas - 1, coluna + 1); e.preventDefault(); }
      else if (e.key === 'Tab') {
        if (e.shiftKey) coluna = Math.max(0, coluna - 1);
        else coluna = Math.min(totalColunas - 1, coluna + 1);
        e.preventDefault();
      }
      else if (e.key === 'Enter') { linha = Math.min(totalLinhas - 1, linha + 1); e.preventDefault(); }
      else if (e.key === 'Escape') { setCelulaAtiva(null); e.preventDefault(); return; }
      else return;
      setCelulaAtiva({ linha, coluna });
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [celulaAtiva, dados]);

  const t = dados?.totais;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: C.bg, display: 'flex', flexDirection: 'column' }}>
      <ModuleHeader
        titulo="Resumo Geral"
        subtitulo="Consolidado por SE e ciclo"
        icone={<TimelineIcon sx={{ fontSize: 28 }} />}
        accentColor="#7C3AED"
        onVoltar={onVoltar}
      />
      <Box sx={{ p: 2, maxWidth: '100%' }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select label="Ciclo" value={cicloId} onChange={e => setCicloId(e.target.value)} size="small" sx={FIELD_SX}>
            <MenuItem value="">Todos os ciclos</MenuItem>
            {ciclos.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.nome}</MenuItem>)}
          </TextField>
          <TextField select label="Área" value={areaId} onChange={e => setAreaId(e.target.value)} size="small" sx={FIELD_SX}>
            <MenuItem value="">Todas as áreas</MenuItem>
            {areas.map(a => <MenuItem key={a.id} value={String(a.id)}>{a.nome}</MenuItem>)}
          </TextField>
          <TextField select label="Agente" value={agenteId} onChange={e => setAgenteId(e.target.value)} size="small" sx={{ ...FIELD_SX, minWidth: 200 }}>
            <MenuItem value="">Todos os agentes</MenuItem>
            {agentes.map(a => <MenuItem key={a.id} value={String(a.id)}>{a.nome} ({a.matricula})</MenuItem>)}
          </TextField>
          <TextField select label="SE" value={seFiltro} onChange={e => setSeFiltro(e.target.value)} size="small" sx={{ ...FIELD_SX, minWidth: 120 }}>
            <MenuItem value="">Todas as SEs</MenuItem>
            {(dados?.ses_disponiveis ?? []).map((s: number) => (
              <MenuItem key={s} value={String(s)}>SE {s}</MenuItem>
            ))}
          </TextField>
        </Box>
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
        {carregando && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
        {!carregando && dados?.linhas.length === 0 && <Alert severity="info">Nenhum registro encontrado.</Alert>}
        {dados && dados.linhas.length > 0 && !carregando && (
          <TableContainer component={Paper} sx={{
            borderRadius: 3.5,
            border: `1px solid ${C.border}`,
            overflow: 'auto',
            maxHeight: 'calc(100vh - 180px)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.03)',
            background: `linear-gradient(180deg, ${C.bgPanel} 0%, #050e1c 100%)`,
          }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {COL_GROUPS.map(g => (
                    <TableCell key={g.label} colSpan={g.cols.length} align="center"
                      sx={{
                        backgroundColor: '#0a1830',
                        backgroundImage: `linear-gradient(180deg, ${g.color}40 0%, ${g.color}1c 100%)`,
                        color: C.white,
                        fontWeight: 800,
                        fontSize: 10.5,
                        fontFamily: "'Inter', sans-serif",
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                        borderBottom: `2px solid ${g.color}`,
                        borderRight: `2px solid rgba(255,255,255,0.25)`,
                        py: 0.7,
                        px: 1.5,
                        whiteSpace: 'nowrap',
                        textShadow: `0 0 8px ${g.color}55`,
                        position: 'sticky', top: 0, zIndex: 3,
                      }}>
                      {g.label}
                    </TableCell>
                  ))}
                  <TableCell colSpan={2} align="center"
                    sx={{
                      backgroundColor: '#0a1830',
                      backgroundImage: `linear-gradient(180deg, ${C.cyan}40 0%, ${C.cyan}1c 100%)`,
                      color: C.white,
                      fontWeight: 800,
                      fontSize: 10.5,
                      fontFamily: "'Inter', sans-serif",
                      textTransform: 'uppercase',
                      letterSpacing: '0.12em',
                      borderBottom: `2px solid ${C.cyan}`,
                      borderRight: `2px solid rgba(255,255,255,0.25)`,
                      py: 0.7,
                      px: 1.5,
                      textShadow: `0 0 8px ${C.cyan}55`,
                      position: 'sticky', top: 0, zIndex: 3,
                    }}>PRODUCAO</TableCell>
                </TableRow>
                <TableRow>
                  {COL_GROUPS.map(g => g.cols.map((c: any) => (
                    <TableCell key={c.key} align="center"
                      sx={{
                        ...(c.key === 'se' ? {
                          backgroundColor: '#0a1830',
                          color: C.amber,
                          fontWeight: 700,
                          fontSize: 10.5,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          fontFamily: "'Inter', sans-serif",
                          borderBottom: `1px solid ${C.cyanBorder}`,
                          borderRight: `2px solid ${C.cyanBorder}`,
                        } : c.key === '_periodo' ? PERIODO_HEADER_SX : {
                          backgroundColor: '#0a1830',
                          backgroundImage: `linear-gradient(180deg, ${g.color}40 0%, ${g.color}1c 100%)`,
                          color: C.white,
                          fontWeight: 700,
                          fontSize: 10.5,
                          fontFamily: "'Inter', sans-serif",
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          borderBottom: `2px solid ${g.color}`,
                          borderRight: `2px solid rgba(255,255,255,0.25)`,
                        }),
                        px: 0.8, py: 0.5, whiteSpace: 'nowrap', position: 'sticky', top: 32, zIndex: 2,
                      }}>
                      {c.label}
                    </TableCell>
                  )))}
                  <TableCell align="center" sx={{
                    backgroundColor: '#0a1830',
                    backgroundImage: `linear-gradient(180deg, ${C.cyan}40 0%, ${C.cyan}1c 100%)`,
                    color: C.white, fontWeight: 700, fontSize: 10.5,
                    fontFamily: "'Inter', sans-serif",
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                    borderBottom: `1px solid ${C.cyan}`,
                    borderRight: `2px solid rgba(255,255,255,0.25)`,
                    px: 0.8, py: 0.5, whiteSpace: 'nowrap', position: 'sticky', top: 32, zIndex: 2,
                  }}>HD.T</TableCell>
                  <TableCell align="center" sx={{
                    backgroundColor: '#0a1830',
                    backgroundImage: `linear-gradient(180deg, ${C.cyan}40 0%, ${C.cyan}1c 100%)`,
                    color: C.white, fontWeight: 700, fontSize: 10.5,
                    fontFamily: "'Inter', sans-serif",
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                    borderBottom: `1px solid ${C.cyan}`,
                    borderRight: `2px solid rgba(255,255,255,0.25)`,
                    px: 0.8, py: 0.5, whiteSpace: 'nowrap', position: 'sticky', top: 32, zIndex: 2,
                  }}>Med./Ag</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dados.linhas.map((l, idxLinha) => (
                  <TableRow key={l.se} sx={{
                    background: idxLinha % 2 === 0 ? C.bgRow : C.bgRowAlt,
                    '&:hover': { background: 'rgba(0,184,204,0.06)' },
                    transition: 'background 150ms ease',
                  }}>
                    {COL_GROUPS.map(g => g.cols.map((c: any) => {
                      const val = c.key === '_periodo' ? '' : (l as any)[c.key];
                      const idxColuna = ALL_COLS.findIndex((ac: any) => ac.key === c.key);
                      const linhaAtiva = celulaAtiva?.linha === idxLinha;
                      const colunaAtiva = celulaAtiva?.coluna === idxColuna;
                      const ehAtiva = linhaAtiva && colunaAtiva;
                      const naRegua = (linhaAtiva || colunaAtiva) && !ehAtiva;
                      return (
                        <TC
                          key={c.key}
                          align={c.key === 'se' ? 'left' : 'center'}
                          onClick={() => setCelulaAtiva({ linha: idxLinha, coluna: idxColuna })}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: 'transparent',
                            ...(c.key === 'se' ? { fontWeight: 700, color: '#f59e0b !important', backgroundColor: '#0a1727 !important', fontSize: 17, fontFamily: "'Inter', sans-serif", borderRight: `2px solid ${C.cyanBorder}` } : c.key === '_periodo' ? { fontWeight: 700, color: '#f59e0b !important', backgroundColor: '#0a1727 !important', fontSize: 14, fontFamily: "'Inter', sans-serif", borderRight: `2px solid ${C.cyanBorder}` } : (c.cellSx || {})),
                            ...(naRegua ? { backgroundColor: 'rgba(245,158,11,0.15) !important' } : {}),
                            ...(ehAtiva ? { backgroundColor: `${C.orange} !important`, color: '#fff !important', fontWeight: 700 } : {}),
                          }}
                        >
                          {c.fmt(val, l)}
                        </TC>
                      );
                    }))}
                    <TC sx={{ fontWeight: 700, color: C.cyan }}>{l.hdt}</TC>
                    <TC sx={{ fontWeight: 700, color: C.grayLt }}>{l.media_imoveis_agente}</TC>
                  </TableRow>
                ))}
                {t && !seFiltro && (
                  <TableRow sx={{
                    background: '#0d2438',
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 2,
                    borderTop: `2px solid ${C.cyanBorder}`,
                    '& td': {
                      backgroundColor: '#0d2438',
                      color: C.cyan,
                      fontWeight: 800,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '0.85rem',
                      letterSpacing: '0.04em',
                    }
                  }}>
                    <TC align="left" sx={{ color: C.cyan, fontWeight: 800, borderRight: `1px solid ${C.cyanBorder}` }}>TOTAL</TC>
                    <TC align="center" sx={{ color: C.amber, fontWeight: 700, borderRight: `1px solid ${C.cyanBorder}` }}>-</TC>
                    <TC sx={{ fontWeight: 700, borderRight: `1px solid ${C.cyanBorder}` }}>{t.imoveis_informados}</TC>
                    <TC sx={{ fontWeight: 700, borderRight: `1px solid ${C.cyanBorder}` }}>{t.quarteirao_concluido}</TC>
                    <TC sx={{ fontWeight: 700, borderRight: `1px solid ${C.cyanBorder}` }}>{t.total_registros}</TC>
                    {(['imoveis_residencia','imoveis_comercio','imoveis_terreno_baldio','imoveis_ponto_estrategico','imoveis_outros','total_imoveis','trat_focal','trat_perifocal','inspecionados','amostras_coletadas','recusados','fechados','recuperados','dep_a1','dep_a2','dep_b','dep_c','dep_d1','dep_d2','dep_e','dep_total','dep_eliminados','larvicida_total_gramas','dep_tratados','imoveis_positivos','dep_positivos'] as const).map(k => (
                      <TC key={k} sx={{ fontWeight: 700, borderRight: `1px solid ${C.cyanBorder}` }}>{(t as any)[k]}{k === 'larvicida_total_gramas' ? ' g' : ''}</TC>
                    ))}
                    <TC sx={{ fontWeight: 800, borderRight: `1px solid ${C.cyanBorder}` }}>{t.hdt}</TC>
                    <TC sx={{ fontWeight: 800, borderRight: `1px solid ${C.cyanBorder}` }}>{t.media_imoveis_agente}</TC>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
